import { useEffect, useMemo, useState } from 'react'
import { Modal as AntModal, Select, Spin } from 'antd'
import { Button, message } from '@/components/ui'
import axios from '@/lib/axios'

type SourceEventOption = {
  id: string
  name: string
  slug: string
}

type SourceTabOption = {
  id: string
  name: string
  position: number
  updatedAt?: string
}

type SourceTabPayload = {
  name: string
  content: {
    html: string
    design: Record<string, unknown>
  }
}

type SourceEventsResponse = {
  events?: SourceEventOption[]
  errors?: string[]
}

type SourceTabsResponse = {
  tabs?: SourceTabOption[]
  errors?: string[]
}

type SourceTabResponse = {
  tab?: SourceTabPayload
  errors?: string[]
}

export type ImportedSourceTab = {
  sourceEventId: string
  sourceEventName: string
  tab: SourceTabPayload
}

interface AddTabFromEventModalProps {
  open: boolean
  eventSlug: string
  onClose: () => void
  onImport: (payload: ImportedSourceTab) => Promise<boolean> | boolean
}

const AddTabFromEventModal = ({ open, eventSlug, onClose, onImport }: AddTabFromEventModalProps) => {
  const [sourceEvents, setSourceEvents] = useState<SourceEventOption[]>([])
  const [sourceTabs, setSourceTabs] = useState<SourceTabOption[]>([])
  const [selectedEventId, setSelectedEventId] = useState<string>()
  const [selectedTabId, setSelectedTabId] = useState<string>()
  const [loadingEvents, setLoadingEvents] = useState(false)
  const [loadingTabs, setLoadingTabs] = useState(false)
  const [loadingImport, setLoadingImport] = useState(false)

  const selectedEvent = useMemo(
    () => sourceEvents.find((event) => event.id === selectedEventId),
    [sourceEvents, selectedEventId]
  )

  const getErrorMessage = (error: unknown, fallback: string) => {
    const axiosError = error as { response?: { data?: { errors?: string[] } } }
    return axiosError.response?.data?.errors?.join(', ') || fallback
  }

  useEffect(() => {
    if (!open) return

    let cancelled = false
    const loadEvents = async () => {
      setLoadingEvents(true)
      try {
        const response = await axios.get<SourceEventsResponse>(
          `/events/${eventSlug}/informational-tabs/tabs/source_events`
        )
        if (cancelled) return

        const events = response.data.events || []
        setSourceEvents(events)
        setSelectedEventId((current) =>
          current && events.some((event) => event.id === current) ? current : events[0]?.id
        )
      } catch (error) {
        if (!cancelled) {
          setSourceEvents([])
          setSelectedEventId(undefined)
          message.error(getErrorMessage(error, 'Failed to load source events'))
        }
      } finally {
        if (!cancelled) setLoadingEvents(false)
      }
    }

    void loadEvents()

    return () => {
      cancelled = true
    }
  }, [open, eventSlug])

  useEffect(() => {
    if (!open || !selectedEventId) {
      setSourceTabs([])
      setSelectedTabId(undefined)
      return
    }

    let cancelled = false
    const loadTabs = async () => {
      setLoadingTabs(true)
      try {
        const response = await axios.get<SourceTabsResponse>(
          `/events/${eventSlug}/informational-tabs/tabs/source_tabs`,
          {
            params: {
              source_event_id: selectedEventId,
            },
          }
        )
        if (cancelled) return

        const tabs = response.data.tabs || []
        setSourceTabs(tabs)
        setSelectedTabId((current) =>
          current && tabs.some((tab) => tab.id === current) ? current : tabs[0]?.id
        )
      } catch (error) {
        if (!cancelled) {
          setSourceTabs([])
          setSelectedTabId(undefined)
          message.error(getErrorMessage(error, 'Failed to load source tabs'))
        }
      } finally {
        if (!cancelled) setLoadingTabs(false)
      }
    }

    void loadTabs()

    return () => {
      cancelled = true
    }
  }, [open, selectedEventId, eventSlug])

  const resetState = () => {
    setSelectedEventId(undefined)
    setSelectedTabId(undefined)
    setSourceTabs([])
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  const handleImport = async () => {
    if (!selectedEventId || !selectedEvent) {
      message.error('Please select a source event.')
      return
    }

    if (!selectedTabId) {
      message.error('Please select a tab to import.')
      return
    }

    setLoadingImport(true)
    try {
      const response = await axios.get<SourceTabResponse>(
        `/events/${eventSlug}/informational-tabs/tabs/source_tab`,
        {
          params: {
            source_event_id: selectedEventId,
            source_tab_id: selectedTabId,
          },
        }
      )

      const sourceTab = response.data.tab
      if (!sourceTab) {
        message.error('Source tab not found.')
        return
      }

      const imported = await onImport({
        sourceEventId: selectedEventId,
        sourceEventName: selectedEvent.name,
        tab: {
          name: sourceTab.name,
          content: {
            html: sourceTab.content?.html || '',
            design: sourceTab.content?.design || {},
          },
        },
      })
      if (imported !== false) {
        handleClose()
      }
    } catch (error) {
      message.error(getErrorMessage(error, 'Failed to import tab'))
    } finally {
      setLoadingImport(false)
    }
  }

  return (
    <AntModal
      open={open}
      onCancel={handleClose}
      title="Add Tab From Another Event"
      width={640}
      centered
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={handleClose} disabled={loadingImport}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleImport}
            loading={loadingImport}
            disabled={!selectedEventId || !selectedTabId}
          >
            Add Tab
          </Button>
        </div>
      }
    >
      <div className="space-y-4 py-2">
        <div>
          <label className="mb-2 block text-sm font-medium text-black/85">Source Event</label>
          <Select
            value={selectedEventId}
            onChange={(value) => setSelectedEventId(value)}
            loading={loadingEvents}
            options={sourceEvents.map((event) => ({
              value: event.id,
              label: event.name,
            }))}
            placeholder={loadingEvents ? 'Loading events...' : 'Select an event'}
            className="w-full"
            showSearch
            optionFilterProp="label"
            disabled={loadingEvents || sourceEvents.length === 0}
          />
          {!loadingEvents && sourceEvents.length === 0 && (
            <p className="mt-2 text-xs text-black/45">No other events available.</p>
          )}
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-black/85">Source Tab</label>
          {loadingTabs ? (
            <div className="flex h-24 items-center justify-center rounded-lg border bg-gray-50">
              <Spin />
            </div>
          ) : (
            <Select
              value={selectedTabId}
              onChange={(value) => setSelectedTabId(value)}
              options={sourceTabs.map((tab) => ({
                value: tab.id,
                label: tab.name,
              }))}
              placeholder={selectedEventId ? 'Select a tab' : 'Select a source event first'}
              className="w-full"
              showSearch
              optionFilterProp="label"
              disabled={!selectedEventId || sourceTabs.length === 0}
            />
          )}
          {!loadingTabs && selectedEventId && sourceTabs.length === 0 && (
            <p className="mt-2 text-xs text-black/45">No informational tabs found in this event.</p>
          )}
        </div>
      </div>
    </AntModal>
  )
}

export default AddTabFromEventModal
